import { useState } from "react";

import { ITEM_CALCULATED_FIELDS } from "appConfig";

import { Decimal, Money } from "classes/DecimalClasses";

import { DeleteLine } from "components/EditPage/DeleteLine";
import { QuickAddItem } from "components/QuickAddItem";
import { LineTableCell } from "components/formFields/LineTableCell";
import { LineClass } from "components/formFields/line/LineClass";
import { LineCostAdjustment } from "components/formFields/line/LineCostAdjustment";
import { LineDescription } from "components/formFields/line/LineDescription";
import { LineInventoryItemBin } from "components/formFields/line/LineInventoryItemBin";
import { LineJobWorkcenter } from "components/formFields/line/LineJobWorkcenter";
import { LineLotSelect } from "components/formFields/line/LineLotSelect";
import { LineNewQuantityWithLot } from "components/formFields/line/LineNewQuantityWithLot";
import { LineOnHandWithLot } from "components/formFields/line/LineOnHandWithLot";
import { LineQuantityDiff } from "components/formFields/line/LineQuantityDiff";
import { LineSerialsSelect } from "components/formFields/line/LineSerialsSelect";
import { LineUom } from "components/formFields/line/LineUom";

import { afterTouchLine } from "services/sosInventoryService/adjustment/afterTouchLine";
import {
  getItemRecord,
  clearItemDataFromLine,
} from "services/sosInventoryService/domainLogic";
import { updateAdjustmentLineWithItem } from "services/sosInventoryService/inventoryTransaction/domainLogic";
import { fetchCostBasis } from "services/sosInventoryService/inventoryTransaction/domainLogic";
import { setPageDirty } from "services/utility/edit";
import { isLineFieldInError } from "services/utility/errors";
import { formatLotsWithQuantityAndExpiration } from "services/utility/formatting";
import { TYPES } from "services/utility/schema";

import { useItemLocationLots } from "hooks/useItemLocationLots";
import { usePlans } from "hooks/usePlans";

import { OBJECT_TYPES } from "appConstants";
import { LINE_ITEM_FIELDS } from "editConfig";

const OBJECT_TYPE = OBJECT_TYPES.ADJUSTMENT.fullString;

// created this wrapper to make drag and drop code easier to see
export function LineItem(props) {
  return <LineItemDetails {...props} />;
}

function LineItemDetails(props) {
  const {
    objectType,
    record,
    line,
    items,
    itemsCount,
    classes,
    classesCount,
    lineHandler,
    expandItemGroup,
    location,
    jobWorkcenters,
    addItem,
    lineItemsErrors: errors,
    lineReadOnly = false,
    handleDeleteLine,
  } = props;
  const { date } = record;

  const [addNewItem, setAddNewItem] = useState(false);
  const [newItemName, setNewItemName] = useState("");
  const [itemLoading, setItemLoading] = useState(false);
  const { hasAtLeastPlusPlan } = usePlans();

  const rawLots = useItemLocationLots(
    {
      itemId: line.item?.id,
      location: record.location?.name,
      binId: line.bin?.id,
      ...(Boolean(line.bin?.id) && { includeZeroOnhandLotsInBin: true }),
      lotId: line.lot?.id,
    },
    date,
    line.itemDetails.lotTracking
  );

  const lots = rawLots ? formatLotsWithQuantityAndExpiration(rawLots) : null;

  function isInError(field) {
    return isLineFieldInError(field, line.lineNumber, errors);
  }

  function handleValueChange(fieldName, newValue) {
    let newLine;
    if (LINE_ITEM_FIELDS[objectType][TYPES.reference].includes(fieldName)) {
      newLine = { ...line, [fieldName]: { id: newValue } };
    } else {
      newLine = { ...line, [fieldName]: newValue };
    }
    setPageDirty();
    lineHandler({
      type: "update",
      updatedLine: afterTouchLine(newLine, fieldName, record),
    });
  }

  function handleReferenceChange(fieldName, newValue) {
    const newLine = { ...line, [fieldName]: newValue };
    setPageDirty();
    lineHandler({
      type: "update",
      updatedLine: afterTouchLine(newLine, fieldName),
    });
  }

  async function handleItemChange(newItem, userInput) {
    if (!newItem) {
      const updatedLine = clearItemDataFromLine(line);
      setPageDirty();
      lineHandler({ type: "update", updatedLine });
      return;
    }
    if (newItem?.id === "add") {
      return openAddItem(userInput);
    }
    setItemLoading(true);
    const item = await getItemRecord(
      newItem.id,
      record.location?.id,
      date,
      ITEM_CALCULATED_FIELDS[objectType]
    );

    const newLine = await updateAdjustmentLineWithItem(item, line, record);
    setPageDirty();
    lineHandler({
      type: "update",
      updatedLine: afterTouchLine(newLine, "item"),
    });
    setItemLoading(false);
  }

  async function setValueDiff(line) {
    const valueDiff = await fetchCostBasis(line, date);
    const updatedLine = { ...line, valueDiff };
    lineHandler({ type: "update", updatedLine });
  }

  function handleUomChange(_, uom) {
    setPageDirty();
    const oldUom = line.uom;
    const updatedLine = afterTouchLine({ ...line, uom, oldUom }, "uom");
    lineHandler({ type: "update", updatedLine });
  }

  function handleDecimalChange(fieldName, newValue) {
    const newLine = {
      ...line,
      [fieldName]: new Decimal(newValue || 0),
    };
    const updatedLine = afterTouchLine(newLine, fieldName);
    setPageDirty();
    lineHandler({ type: "update", updatedLine });
    setValueDiff(updatedLine);
  }

  function handleNewQuantityWithLotChange(fieldName, newValue) {
    const newLine = {
      ...line,
      lots,
      newLotQuantity: new Decimal(newValue || 0),
    };
    const updatedLine = afterTouchLine(newLine, fieldName);
    setPageDirty();
    lineHandler({ type: "update", updatedLine });
    setValueDiff(updatedLine);
  }

  function handleMoneyChange(fieldName, newValue) {
    const newLine = {
      ...line,
      [fieldName]: new Money(newValue || 0),
    };
    const updatedLine = afterTouchLine(newLine, fieldName);
    lineHandler({ type: "update", updatedLine });
  }

  async function onAddItem(item) {
    setAddNewItem(false);
    await handleItemChange(item);
    addItem();
  }

  function handleDelete() {
    if (handleDeleteLine) {
      handleDeleteLine(line.lineNumber);
    } else {
      setPageDirty();
      lineHandler({ type: "delete", deleteAt: line.lineNumber });
    }
  }

  function openAddItem(userInput) {
    // handle "Add new item" in Item select input field; this does not change the
    // line item, so no need to proceed further into the handler
    setNewItemName(userInput);
    setAddNewItem(true);
  }

  if (!line) {
    return null;
  }

  return (
    <>
      <DeleteLine onClick={handleDelete} />
      <LineTableCell>
        <div
          style={{
            cursor: "pointer",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "0 0.5rem",
          }}
        >
          {line.lineNumber}
        </div>
      </LineTableCell>
      <LineInventoryItemBin
        allowExpand
        itemLoading={itemLoading}
        bins={line.availableBins}
        location={location}
        handleItemChange={handleItemChange}
        handleReferenceChange={handleReferenceChange}
        objectType={OBJECT_TYPE}
        line={line}
        items={items}
        itemsCount={itemsCount}
        addItem={addItem}
        expandItemGroup={expandItemGroup}
        isInError={isInError}
        lineReadOnly={lineReadOnly}
      />
      <LineTableCell sx={{ minWidth: "24rem" }}>
        <LineDescription
          value={line.description}
          onValueChange={handleValueChange}
          error={isInError("description")}
          lineReadOnly={lineReadOnly}
        />
        {hasAtLeastPlusPlan && (
          <>
            {line.itemDetails?.serialTracking && (
              <LineSerialsSelect
                objectType={objectType}
                value={line.serials}
                itemId={line.item.id}
                bin={line.bin}
                location={location}
                quantity={line.quantityDiff || new Decimal(0)}
                onValueChange={handleReferenceChange}
                error={isInError("serials")}
                enableForNegativeQuantity
                lineReadOnly={lineReadOnly}
              />
            )}

            {line.itemDetails?.lotTracking && (
              <LineLotSelect
                value={line.lot}
                lots={lots}
                bin={line.bin}
                objectType={objectType}
                location={location}
                onValueChange={handleReferenceChange}
                lineReadOnly={lineReadOnly}
              />
            )}
          </>
        )}
      </LineTableCell>
      <LineOnHandWithLot
        lotTracked={line.itemDetails.lotTracking}
        onhand={line.onhand}
        lot={line.lot}
        lots={lots}
        lineNumber={line.lineNumber}
        item={line.item}
        lineReadOnly={lineReadOnly}
      />
      <LineNewQuantityWithLot
        lotTracked={line.itemDetails.lotTracking}
        quantityDiff={line.quantityDiff}
        lot={line.lot}
        lots={lots}
        value={line.newQuantity}
        objectType={objectType}
        onValueChange={handleDecimalChange}
        onValueWithLotChange={handleNewQuantityWithLotChange}
        error={isInError("newQuantity")}
        disabled={itemLoading}
        lineNumber={line.lineNumber}
        item={line.item}
        lineReadOnly={lineReadOnly}
      />
      <LineQuantityDiff
        value={line.quantityDiff}
        objectType={objectType}
        onValueChange={handleDecimalChange}
        error={isInError("quantityDiff")}
        lineReadOnly={lineReadOnly}
      />
      <LineUom
        value={line.uom}
        onValueChange={handleUomChange}
        itemUoms={line.itemDetails?.itemUoms}
        error={isInError("uom")}
        lineReadOnly={lineReadOnly}
      />
      <LineCostAdjustment
        value={line.valueDiff}
        objectType={objectType}
        onValueChange={handleMoneyChange}
        error={isInError("valueDiff")}
        lineReadOnly={lineReadOnly}
      />
      <LineClass
        value={line.class}
        classes={classes}
        classesCount={classesCount}
        onValueChange={handleReferenceChange}
        lineReadOnly={lineReadOnly}
      />
      <LineJobWorkcenter
        job={line.job}
        workcenter={line.workcenter}
        value={line.jobWorkcenter}
        jobWorkcenters={jobWorkcenters}
        onValueChange={handleReferenceChange}
        lineReadOnly={lineReadOnly}
      />
      {addNewItem && (
        <QuickAddItem
          open={addNewItem}
          initialNameValue={newItemName}
          onClose={() => setAddNewItem(false)}
          onAdd={onAddItem}
        />
      )}
    </>
  );
}
